import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, Package, Plus, Minus, ShoppingBag, Calendar, FileText, Download, Upload, Image, Eye, Clock, Filter, History, Sparkles, Loader2, RotateCcw, Pencil, Ban } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import CalendarioFases from './CalendarioFases';

const withAuthToken = (url) => {
    const token = localStorage.getItem('token');
    return token ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;
};

// Formato venezolano: 60.325,60
const formatMoney = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0,00';
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const VentaEspecialTienda = () => {
    const [activeVenta, setActiveVenta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cart, setCart] = useState({});
    const [editandoPedido, setEditandoPedido] = useState(false);
    const [anulando, setAnulando] = useState(false);
    const [pesos, setPesos] = useState({}); // bolsa por kg: {productoId: peso}
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('calendario'); // calendario, info, tienda
    const [uploadingComprobante, setUploadingComprobante] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    // Filtro por categoría
    const [selectedCategory, setSelectedCategory] = useState('todas');

    // Historial
    const [showHistorial, setShowHistorial] = useState(false);
    const [historial, setHistorial] = useState([]);
    const [historialLoading, setHistorialLoading] = useState(false);

    // Sugerencia IA
    const [sugerencias, setSugerencias] = useState([]);
    const [sugerenciasLoading, setSugerenciasLoading] = useState(false);

    const [countdown, setCountdown] = useState(null);

    useEffect(() => {
        fetchActiveVenta();
    }, []);

    // Auto-switch tab when order exists
    useEffect(() => {
        if (activeVenta?.mi_pedido && activeVenta.mi_pedido.estado !== 'borrador') {
            setActiveTab('tienda');
        }
    }, [activeVenta]);

    // Countdown timer for payment deadline
    useEffect(() => {
        if (!activeVenta?.fechaLimitePago) { setCountdown(null); return; }
        const target = new Date(activeVenta.fechaLimitePago).getTime();
        const tick = () => {
            const now = Date.now();
            const diff = target - now;
            if (diff <= 0) { setCountdown({ expired: true }); return; }
            setCountdown({
                dias: Math.floor(diff / (1000 * 60 * 60 * 24)),
                horas: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutos: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
                expired: false
            });
        };
        tick();
        const interval = setInterval(tick, 60000); // update every minute
        return () => clearInterval(interval);
    }, [activeVenta?.fechaLimitePago]);

    const fetchActiveVenta = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/ventas-especiales/activa');
            if (response.data.activa === false || !response.data.id) {
                setActiveVenta(null);
            } else {
                setActiveVenta(response.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const addToCart = (producto, delta) => {
        const currentQty = cart[producto.id] || 0;
        const newQty = currentQty + delta;
        const maxPorEmpleado = producto.limitePorEmpleado || Infinity;
        const maxStock = producto.stockDisponible ?? Infinity;
        const maxPermitido = Math.min(maxPorEmpleado, maxStock);

        if (newQty <= 0) {
            const newCart = { ...cart };
            delete newCart[producto.id];
            setCart(newCart);
        } else {
            setCart({ ...cart, [producto.id]: Math.min(newQty, maxPermitido) });
        }
    };

    // Categorías disponibles
    const categorias = useMemo(() => {
        if (!activeVenta?.productos) return [];
        const cats = new Set(activeVenta.productos.map(p => p.categoria).filter(Boolean));
        return Array.from(cats).sort();
    }, [activeVenta?.productos]);

    // Productos filtrados
    const productosFiltrados = useMemo(() => {
        if (!activeVenta?.productos) return [];
        if (selectedCategory === 'todas') return activeVenta.productos;
        return activeVenta.productos.filter(p => p.categoria === selectedCategory);
    }, [activeVenta?.productos, selectedCategory]);

    // Fetch historial
    const fetchHistorial = async () => {
        try {
            setHistorialLoading(true);
            const response = await axiosInstance.get('/api/ventas-especiales/historial');
            setHistorial(response.data.pedidos || []);
        } catch (error) { console.error(error); }
        finally { setHistorialLoading(false); }
    };

    // Fetch sugerencias IA
    const fetchSugerencias = async () => {
        if (!activeVenta?.id) return;
        try {
            setSugerenciasLoading(true);
            const response = await axiosInstance.get(`/api/ventas-especiales/${activeVenta.id}/pedido/sugerencia`);
            setSugerencias(response.data.sugerencias || []);
            // Auto-agregar al carrito
            const newCart = { ...cart };
            for (const s of (response.data.sugerencias || [])) {
                if (s.cantidadSugerida > 0) {
                    newCart[s.productoId] = s.cantidadSugerida;
                }
            }
            setCart(newCart);
        } catch (error) { console.error(error); }
        finally { setSugerenciasLoading(false); }
    };

    const cartTotal = activeVenta?.productos?.reduce((acc, prod) => {
        if (prod.opcionesPeso && prod.opcionesPeso.length > 0) {
            return acc + (cart[prod.id] ? prod.precio * (pesos[prod.id] || 0) : 0);
        }
        const qty = cart[prod.id] || 0;
        return acc + (prod.precio * qty);
    }, 0) || 0;

    const cartItemsCount = Object.values(cart).reduce((a, b) => a + b, 0);

    const handleSubmitOrder = async () => {
        if (cartItemsCount === 0) return;

        try {
            setSubmitting(true);
            const items = Object.entries(cart).map(([prodId, qty]) => ({
                productoId: parseInt(prodId),
                cantidad: qty,
                peso: pesos[prodId] || null
            }));

            await axiosInstance.post(`/api/ventas-especiales/${activeVenta.id}/pedido`, {
                items
            });

            setCart({});
            setPesos({});
            setEditandoPedido(false);
            fetchActiveVenta(); // Recargar para ver el pedido confirmado
        } catch (error) {
            alert(error.response?.data?.error || 'Error enviando pedido');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditarPedido = () => {
        const nuevoCart = {};
        const nuevoPesos = {};
        (activeVenta?.mi_pedido?.items || []).forEach(it => { nuevoCart[it.productoId] = it.cantidad; if (it.peso) nuevoPesos[it.productoId] = it.peso; });
        setCart(nuevoCart);
        setPesos(nuevoPesos);
        setEditandoPedido(true);
        setActiveTab('tienda');
    };

    const handleAnularPedido = async () => {
        if (!window.confirm('¿Seguro que quieres anular tu pedido? Podrás hacer uno nuevo mientras la venta siga abierta.')) return;
        try {
            setAnulando(true);
            await axiosInstance.post(`/api/ventas-especiales/${activeVenta.id}/pedido/anular`);
            setCart({});
            setPesos({});
            setEditandoPedido(false);
            fetchActiveVenta();
        } catch (error) {
            alert(error.response?.data?.error || 'Error anulando pedido');
        } finally {
            setAnulando(false);
        }
    };

    const handleUploadComprobante = async () => {
        if (!selectedFile || !activeVenta) return;
        try {
            setUploadingComprobante(true);
            const formData = new FormData();
            formData.append('comprobante', selectedFile);
            await axiosInstance.post(
                `/api/ventas-especiales/${activeVenta.id}/pedido/comprobante`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setSelectedFile(null);
            fetchActiveVenta();
        } catch (error) {
            alert(error.response?.data?.error || 'Error subiendo comprobante');
        } finally {
            setUploadingComprobante(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-teal-600 rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    if (!activeVenta) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                    <Package size={48} className="mx-auto text-gray-300 mb-4" />
                    <h2 className="text-xl font-bold text-gray-900">No hay ventas especiales activas</h2>
                    <p className="text-gray-500 mt-2">Te notificaremos cuando inicie la próxima venta.</p>
                </div>
            </div>
        );
    }

    const miPedido = activeVenta.mi_pedido;
    const yaHizoPedido = miPedido && !['borrador', 'cancelado'].includes(miPedido.estado);

    const renderTienda = () => {
        if (activeVenta.bloqueado) {
            return (
                <div className="p-6">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
                        <Ban size={40} className="mx-auto text-red-400 mb-3" />
                        <h2 className="text-lg font-bold text-red-800">Estás bloqueado para esta compra</h2>
                        <p className="text-sm text-red-600 mt-2">{activeVenta.motivoBloqueo && <>Motivo: {activeVenta.motivoBloqueo}. </>}Contacta a Costos para más información.</p>
                    </div>
                </div>
            );
        }

        if (yaHizoPedido && !editandoPedido) {
            const estadoConfig = {
                pendiente: { bg: 'bg-amber-50', border: 'border-amber-200', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', titleColor: 'text-amber-900', subtitleColor: 'text-amber-700', title: 'Pedido Registrado — Pendiente de Pago', subtitle: 'Sube tu comprobante de pago para completar el proceso' },
                pagado: { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-100', iconColor: 'text-green-600', titleColor: 'text-green-900', subtitleColor: 'text-green-700', title: 'Pago Registrado', subtitle: 'Tu comprobante ha sido recibido' },
                entregado: { bg: 'bg-purple-50', border: 'border-purple-200', iconBg: 'bg-purple-100', iconColor: 'text-purple-600', titleColor: 'text-purple-900', subtitleColor: 'text-purple-700', title: 'Pedido Entregado', subtitle: 'Tu pedido ha sido entregado exitosamente' },
                // Pagado y entregado: Procura lo marca así cuando el pedido terminó
                completado: { bg: 'bg-green-50', border: 'border-green-200', iconBg: 'bg-green-100', iconColor: 'text-green-600', titleColor: 'text-green-900', subtitleColor: 'text-green-700', title: 'Pago Completado', subtitle: 'Tu pago fue verificado y tu pedido ya fue entregado' },
                cancelado: { bg: 'bg-gray-50', border: 'border-gray-200', iconBg: 'bg-gray-100', iconColor: 'text-gray-500', titleColor: 'text-gray-900', subtitleColor: 'text-gray-600', title: 'Pedido Cancelado', subtitle: 'Este pedido fue cancelado' },
            };
            const config = estadoConfig[miPedido.estado] || estadoConfig.pendiente;

            return (
                <div className="p-6">
                    {/* Header del estado */}
                    <div className={`${config.bg} border ${config.border} rounded-xl p-6 mb-6`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-10 h-10 rounded-full ${config.iconBg} flex items-center justify-center`}>
                                <CheckCircle className={config.iconColor} size={20} />
                            </div>
                            <div>
                                <h2 className={`font-bold ${config.titleColor}`}>{config.title}</h2>
                                <p className={`text-sm ${config.subtitleColor}`}>{config.subtitle}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div className="bg-white rounded-lg p-4 border border-gray-100">
                                <p className="text-sm text-gray-500">{['pagado', 'completado'].includes(miPedido.estado) ? 'Total pagado' : 'Total a pagar'}</p>
                                <p className="text-2xl font-bold text-gray-900">Bs {formatMoney(miPedido.totalCalculado)}</p>
                            </div>
                            {activeVenta.fechaLimitePago && (
                                <div className="bg-white rounded-lg p-4 border border-gray-100">
                                    <p className="text-sm text-gray-500">Fecha límite de pago</p>
                                    <p className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                        <Calendar size={16} className="text-gray-400" />
                                        {new Date(activeVenta.fechaLimitePago).toLocaleDateString('es-VE', { day: 'numeric', month: 'long' })}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Countdown de fecha límite de pago */}
                    {countdown && miPedido.estado === 'pendiente' && (() => {
                        let urgencia = 'teal';
                        if (countdown.expired) urgencia = 'red';
                        else if (countdown.dias <= 1) urgencia = 'red';
                        else if (countdown.dias <= 3) urgencia = 'amber';

                        const colores = {
                            teal: { bg: 'from-cyan-500 to-blue-600', pill: 'bg-cyan-100 text-cyan-700', text: 'text-cyan-600' },
                            amber: { bg: 'from-amber-500 to-orange-600', pill: 'bg-amber-100 text-amber-700', text: 'text-amber-600' },
                            red: { bg: 'from-red-500 to-rose-600', pill: 'bg-red-100 text-red-700', text: 'text-red-600' }
                        };
                        const c = colores[urgencia];

                        return (
                            <div className={`bg-gradient-to-r ${c.bg} rounded-xl p-5 mb-6 text-white`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                            <Clock size={20} />
                                        </div>
                                        <div>
                                            <p className="font-bold text-lg">
                                                {countdown.expired ? '¡Plazo vencido!' : 'Fecha límite de pago'}
                                            </p>
                                            <p className="text-white/80 text-sm">
                                                {new Date(activeVenta.fechaLimitePago).toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long' })}
                                            </p>
                                        </div>
                                    </div>
                                    {!countdown.expired && (
                                        <div className="flex items-center gap-3">
                                            {countdown.dias > 0 && (
                                                <div className="text-center">
                                                    <div className="text-3xl font-bold">{countdown.dias}</div>
                                                    <div className="text-xs text-white/70 uppercase">días</div>
                                                </div>
                                            )}
                                            <div className="text-center">
                                                <div className="text-3xl font-bold">{countdown.horas}</div>
                                                <div className="text-xs text-white/70 uppercase">horas</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-3xl font-bold">{String(countdown.minutos).padStart(2, '0')}</div>
                                                <div className="text-xs text-white/70 uppercase">min</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Sección de Comprobante de Pago */}
                    {miPedido.estado === 'pendiente' && (
                        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Upload size={18} className="text-teal-600" />
                                Registrar Pago
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                                Sube una captura o foto de tu comprobante de pago (transferencia, pago móvil, etc.)
                            </p>

                            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-teal-400 transition-colors">
                                {selectedFile ? (
                                    <div className="space-y-4">
                                        {selectedFile.type.startsWith('image/') ? (
                                            <img
                                                src={URL.createObjectURL(selectedFile)}
                                                alt="Preview"
                                                className="max-h-48 mx-auto rounded-lg shadow-sm"
                                            />
                                        ) : (
                                            <div className="flex items-center justify-center gap-2 text-gray-600">
                                                <FileText size={24} />
                                                <span className="font-medium">{selectedFile.name}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-center gap-3">
                                            <button
                                                onClick={() => setSelectedFile(null)}
                                                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                            >
                                                Cambiar archivo
                                            </button>
                                            <button
                                                onClick={handleUploadComprobante}
                                                disabled={uploadingComprobante}
                                                className="px-6 py-2 text-sm text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 font-medium"
                                            >
                                                {uploadingComprobante ? 'Enviando...' : 'Enviar Comprobante'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <label className="cursor-pointer block">
                                        <input
                                            type="file"
                                            accept="image/*,.pdf"
                                            className="hidden"
                                            onChange={(e) => setSelectedFile(e.target.files[0])}
                                        />
                                        <Image size={32} className="mx-auto text-gray-400 mb-2" />
                                        <p className="text-sm font-medium text-gray-700">Haz clic para seleccionar archivo</p>
                                        <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP o PDF</p>
                                    </label>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Comprobante ya subido */}
                    {miPedido.comprobantePago && ['pagado', 'entregado', 'completado'].includes(miPedido.estado) && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                        <Image size={20} className="text-green-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-green-900">Comprobante de pago</p>
                                        <p className="text-xs text-green-600">Archivo recibido correctamente</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <a
                                        href={withAuthToken(`/api/ventas-especiales/${activeVenta.id}/pedido/comprobante/${miPedido.comprobantePago}`)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="px-3 py-2 text-sm text-teal-700 bg-white border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors flex items-center gap-1"
                                    >
                                        <Eye size={16} /> Ver
                                    </a>
                                    {miPedido.estado === 'pagado' && (
                                        <label className="px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1 cursor-pointer">
                                            <Upload size={16} /> Resubir
                                            <input
                                                type="file"
                                                accept="image/*,.pdf"
                                                className="hidden"
                                                onChange={(e) => {
                                                    setSelectedFile(e.target.files[0]);
                                                    // Trigger upload change
                                                }}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>
                            {/* Si seleccionó archivo para resubir */}
                            {selectedFile && (
                                <div className="mt-4 pt-4 border-t border-green-200 flex items-center justify-between">
                                    <p className="text-sm text-gray-700">Nuevo archivo: <span className="font-medium">{selectedFile.name}</span></p>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => setSelectedFile(null)} className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700">Cancelar</button>
                                        <button
                                            onClick={handleUploadComprobante}
                                            disabled={uploadingComprobante}
                                            className="px-4 py-1 text-sm text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                                        >
                                            {uploadingComprobante ? 'Enviando...' : 'Actualizar Comprobante'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <h3 className="font-bold text-gray-900 mb-3">Detalle de tu pedido</h3>
                    <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-100 border-b border-gray-200">
                                <tr>
                                    <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-2">Producto</th>
                                    <th className="text-center text-xs font-semibold text-gray-600 uppercase px-4 py-2">Cantidad</th>
                                    <th className="text-right text-xs font-semibold text-gray-600 uppercase px-4 py-2">Precio</th>
                                    <th className="text-right text-xs font-semibold text-gray-600 uppercase px-4 py-2">Subtotal</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {miPedido.items?.map((item, idx) => (
                                    <tr key={idx}>
                                        <td className="px-4 py-3 text-sm text-gray-900">{item.productoDescripcion}</td>
                                        <td className="px-4 py-3 text-sm text-center font-bold">{item.peso ? `${item.peso} kg` : item.cantidad}</td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-600">Bs {formatMoney(item.precioUnitario)}</td>
                                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">Bs {formatMoney(item.subtotal)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {miPedido.estado === 'pendiente' && (!activeVenta.fechaLimitePedido || new Date(activeVenta.fechaLimitePedido) > new Date()) && (
                        <div className="flex flex-wrap gap-3 mt-6">
                            <button
                                onClick={handleEditarPedido}
                                className="flex items-center gap-2 px-5 py-2.5 bg-white text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors font-medium text-sm"
                            >
                                <Pencil size={16} /> Editar pedido
                            </button>
                            <button
                                onClick={handleAnularPedido}
                                disabled={anulando}
                                className="flex items-center gap-2 px-5 py-2.5 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors font-medium text-sm disabled:opacity-50"
                            >
                                <Ban size={16} /> {anulando ? 'Anulando...' : 'Anular pedido'}
                            </button>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div>
                {editandoPedido && (
                    <div className="mx-1 mb-3 p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-center justify-between gap-3">
                        <p className="text-sm text-teal-800 font-medium flex items-center gap-2"><Pencil size={15} /> Estás editando tu pedido. Ajusta las cantidades y guarda los cambios.</p>
                        <button onClick={() => { setCart({}); setPesos({}); setEditandoPedido(false); }} className="text-sm text-teal-700 hover:text-teal-900 font-medium whitespace-nowrap">Cancelar edición</button>
                    </div>
                )}
                {/* Barra de filtros y acciones */}
                <div className="flex flex-wrap items-center gap-2 mb-4 px-1">
                    {/* Filtro categorías */}
                    {categorias.length > 1 && (
                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                            <button
                                onClick={() => setSelectedCategory('todas')}
                                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${selectedCategory === 'todas' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Todas
                            </button>
                            {categorias.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${selectedCategory === cat ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="flex-1" />

                    {/* Botón historial */}
                    <button
                        onClick={() => { setShowHistorial(!showHistorial); if (!showHistorial && historial.length === 0) fetchHistorial(); }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${showHistorial ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                        <History size={14} />
                        Mis compras
                    </button>

                    {/* Lista de articulos y precios en Excel */}
                    <a
                        href={withAuthToken(`/api/ventas-especiales/${activeVenta.id}/lista-precios`)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200"
                    >
                        <Download size={14} />
                        Descargar lista
                    </a>

                    {/* Botón repetir pedido IA */}
                    <button
                        onClick={fetchSugerencias}
                        disabled={sugerenciasLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-200 transition-colors disabled:opacity-50"
                    >
                        {sugerenciasLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        Repetir pedido
                    </button>
                </div>

                {/* Sugerencias IA */}
                {sugerencias.length > 0 && (
                    <div className="mb-4 bg-purple-50 border border-purple-200 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles size={14} className="text-purple-500" />
                            <span className="text-xs font-semibold text-purple-700">Sugerencias basadas en tus compras anteriores</span>
                            <button onClick={() => setSugerencias([])} className="ml-auto text-purple-400 hover:text-purple-600"><span className="text-xs">Cerrar</span></button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {sugerencias.map(s => (
                                <div key={s.productoId} className="bg-white rounded-lg px-3 py-1.5 border border-purple-100 text-xs flex items-center gap-2">
                                    <span className="font-medium text-gray-900">{s.descripcion}</span>
                                    <span className="text-purple-600">×{s.cantidadSugerida}</span>
                                    {s.razon && <span className="text-gray-400">({s.razon})</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Historial de compras */}
                {showHistorial && (
                    <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                            <History size={16} />Mis compras anteriores
                        </h4>
                        {historialLoading ? (
                            <div className="text-center py-4"><Loader2 size={20} className="animate-spin text-indigo-400 mx-auto" /></div>
                        ) : historial.length === 0 ? (
                            <p className="text-sm text-indigo-600">No tienes compras anteriores.</p>
                        ) : (
                            <div className="space-y-2">
                                {historial.map(p => (
                                    <div key={p.id} className="bg-white rounded-lg p-3 border border-indigo-100">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-900">{p.ventaTitulo || 'Venta'}</span>
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                                ['entregado', 'completado'].includes(p.estado) ? 'bg-green-100 text-green-700' :
                                                p.estado === 'pagado' ? 'bg-blue-100 text-blue-700' :
                                                'bg-gray-100 text-gray-600'
                                            }`}>{p.estado}</span>
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {new Date(p.fechaPedido).toLocaleDateString()} — Bs {formatMoney(p.totalCalculado)}
                                            {p.items && <span className="ml-2">({p.items.length} productos)</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Código</th>
                            <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Producto</th>
                            <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Observaciones</th>
                            <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Unidad</th>
                            <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Precio (Bs)</th>
                            <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3 w-32">Cantidad</th>
                            <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Subtotal</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {productosFiltrados.map(prod => {
                            const esGranel = prod.opcionesPeso && prod.opcionesPeso.length > 0;
                            const qty = cart[prod.id] || 0;
                            const pesoSel = pesos[prod.id] || '';
                            const subtotal = esGranel ? prod.precio * (parseFloat(pesoSel) || 0) : prod.precio * qty;
                            const stockDisponible = prod.stockDisponible ?? Infinity;
                            const canAdd = qty < stockDisponible && (!prod.limitePorEmpleado || qty < prod.limitePorEmpleado);

                            return (
                                <tr key={prod.id} className={`transition-colors ${qty > 0 ? 'bg-teal-50/50' : 'hover:bg-gray-50'}`}>
                                    <td className="px-4 py-3 text-sm font-mono text-gray-600">{prod.codigo}</td>
                                    <td className="px-4 py-3">
                                        <div className="text-sm text-gray-900 font-medium">{prod.descripcion}</div>
                                        {prod.limitePorEmpleado && (
                                            <div className="text-xs text-orange-600">Máx. {prod.limitePorEmpleado} por persona</div>
                                        )}
                                        {Number.isFinite(stockDisponible) && stockDisponible <= 5 && (
                                            <div className="text-xs text-red-600">Disponible: {stockDisponible}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{prod.observaciones || '-'}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500 text-center">{prod.unidadManejo || 'Unidad'}</td>
                                    <td className="px-4 py-3 text-sm text-right font-bold text-teal-600">Bs {formatMoney(prod.precio)}</td>
                                    <td className="px-4 py-3">
                                        {esGranel ? (
                                            <select
                                                value={pesoSel}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (!val) {
                                                        setCart(prev => { const n = { ...prev }; delete n[prod.id]; return n; });
                                                        setPesos(prev => { const n = { ...prev }; delete n[prod.id]; return n; });
                                                    } else {
                                                        setCart(prev => ({ ...prev, [prod.id]: 1 }));
                                                        setPesos(prev => ({ ...prev, [prod.id]: parseFloat(val) }));
                                                    }
                                                }}
                                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
                                            >
                                                <option value="">Elegir kg...</option>
                                                {prod.opcionesPeso.map((p, i) => (
                                                    <option key={i} value={p}>{p} kg</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <div className="flex items-center justify-center gap-1">
                                                <button onClick={() => addToCart(prod, -1)} disabled={qty === 0} className="w-7 h-7 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"><Minus size={14} /></button>
                                                <span className="w-8 text-center font-bold text-gray-900">{qty}</span>
                                                <button onClick={() => addToCart(prod, 1)} disabled={!canAdd} className="w-7 h-7 rounded bg-teal-100 hover:bg-teal-200 text-teal-700 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"><Plus size={14} /></button>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                                        {subtotal > 0 ? `Bs ${formatMoney(subtotal)}` : '-'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {cartItemsCount > 0 && (
                    <div className="p-5 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                        <div>
                            <p className="text-sm text-gray-500">{cartItemsCount} producto(s) seleccionado(s)</p>
                            <p className="text-2xl font-bold text-gray-900">Total: Bs {formatMoney(cartTotal)}</p>
                        </div>
                        <button
                            onClick={handleSubmitOrder}
                            disabled={submitting}
                            className="px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
                        >
                            {submitting ? 'Guardando...' : (editandoPedido ? 'Guardar cambios' : 'Enviar Pedido')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderCalendario = () => {
        return (
            <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Calendario del Proceso</h3>
                <CalendarioFases diasPintados={activeVenta.fasesCalendario || {}} />
            </div>
        );
    };

    const renderInfoPago = () => {
        const info = activeVenta.infoPago || {};
        const archivos = activeVenta.archivosAdjuntos || [];

        return (
            <div className="p-6 space-y-6">
                {archivos.length > 0 && (
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Archivos y Documentos</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {archivos.map((archivo, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                                            <FileText size={20} className="text-red-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{archivo.originalName}</p>
                                            <p className="text-xs text-gray-500">PDF Document</p>
                                        </div>
                                    </div>
                                    <a
                                        href={withAuthToken(`/api/ventas-especiales/${activeVenta.id}/archivos/${archivo.filename}`)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                                        title="Descargar"
                                    >
                                        <Download size={20} />
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Información para Pagos</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {info.banco && (
                            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                                <h4 className="font-semibold text-gray-900 border-b border-gray-100 pb-2 mb-3">Transferencia Bancaria</h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between"><span className="text-gray-500">Banco:</span> <span className="font-medium text-gray-900">{info.banco}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Cuenta:</span> <span className="font-medium font-mono text-gray-900">{info.cuenta}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">RIF:</span> <span className="font-medium text-gray-900">{info.rif}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Titular:</span> <span className="font-medium text-gray-900">{info.titular}</span></div>
                                </div>
                            </div>
                        )}
                        {info.pagoMovil?.banco && (
                            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                                <h4 className="font-semibold text-gray-900 border-b border-gray-100 pb-2 mb-3">Pago Móvil</h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between"><span className="text-gray-500">Banco:</span> <span className="font-medium text-gray-900">{info.pagoMovil.banco}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Teléfono:</span> <span className="font-medium font-mono text-gray-900">{info.pagoMovil.telefono}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">RIF/Cédula:</span> <span className="font-medium text-gray-900">{info.pagoMovil.rif}</span></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto px-4 pb-12">
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-white">
                    <div className="flex items-center gap-4">
                        <img src="/logo_sura.png" alt="SURA" className="h-12 w-auto" />
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">{activeVenta.titulo}</h1>
                            <p className="text-sm text-gray-500">{activeVenta.descripcion}</p>
                        </div>
                    </div>
                </div>

                {/* Tabs Navigation */}
                <div className="flex gap-6 px-6 border-b border-gray-100 bg-gray-50/50">
                    {[
                        { key: 'calendario', label: 'Calendario', icon: <Calendar size={18} /> },
                        { key: 'info', label: 'Info y Archivos', icon: <FileText size={18} /> },
                        { key: 'tienda', label: yaHizoPedido ? 'Mi Pedido' : 'Realizar Pedido', icon: yaHizoPedido ? <ShoppingBag size={18} /> : <Package size={18} /> },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex items-center gap-2 py-4 text-sm font-semibold transition-colors border-b-2 ${activeTab === tab.key ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div>
                    {activeTab === 'tienda' && renderTienda()}
                    {activeTab === 'calendario' && renderCalendario()}
                    {activeTab === 'info' && renderInfoPago()}
                </div>
            </div>
        </div>
    );
};

export default VentaEspecialTienda;
